import classNames from "classnames";
import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import useOutsideAlerter from "../hooks/use_outside_alerter";

export type WithDropdownBaseRef = {
	close: () => void;
};

export const WithDropdownBase = forwardRef<
	WithDropdownBaseRef,
	{
		children: React.ReactNode | ((is_active: boolean) => React.ReactNode);
		dropdown_element: React.ReactNode;
		x_alignment?: "left" | "right" | "middle" | "auto";
		y_alignment?: "top" | "middle" | "bottom" | "auto";
		id?: string;
		parent_id?: string;
		disabled?: boolean;
		onClose?: () => void;
	}
>((props, ref) => {
	const [is_active, setIsActive] = useState(false);

	const trigger_ref = useRef<HTMLDivElement>(null);
	const dropdown_ref = useRef<HTMLDivElement>(null);

	useOutsideAlerter(dropdown_ref, () => setIsActive(false));

	const close = () => {
		setIsActive(false);
		props.onClose?.();
	};

	useImperativeHandle(ref, () => ({
		close,
	}));

	useEffect(() => {
		const setPosition = () => {
			if (!is_active || !trigger_ref.current || !dropdown_ref.current) {
				return;
			}
			const container = props.parent_id
				? document.getElementById(props.parent_id)
				: document.body;

			// Position dropdown_ref relative to trigger_ref
			const trigger_rect = trigger_ref.current.getBoundingClientRect();
			const dropdown_rect = dropdown_ref.current.getBoundingClientRect();
			const container_rect = container.getBoundingClientRect();

			const dropdown_width = dropdown_rect.width;
			const dropdown_height = dropdown_rect.height;

			let x_alignment = props.x_alignment || "auto";
			const y_alignment = props.y_alignment || "auto";

			let dropdown_center_abs_x =
				trigger_rect.left + trigger_rect.width / 2;

			if (x_alignment === "auto") {
				// For auto alignment, check which side has more space
				if (dropdown_center_abs_x > window.innerWidth / 2) {
					x_alignment = "right";
				} else {
					x_alignment = "left";
				}
			}

			let dropdown_abs_left: number;
			if (x_alignment === "left") {
				// Left align - dropdown's left edge aligns with trigger's left edge
				dropdown_abs_left = trigger_rect.left;
			} else if (x_alignment === "right") {
				// Right align - dropdown's right edge aligns with trigger's right edge
				dropdown_abs_left = trigger_rect.right - dropdown_rect.width;
			} else if (x_alignment === "middle") {
				// Middle align - dropdown is centered with the trigger
				dropdown_abs_left =
					dropdown_center_abs_x - dropdown_rect.width / 2;
			} else {
				// Default to centered
				dropdown_abs_left =
					dropdown_center_abs_x - dropdown_rect.width / 2;
			}

			let dropdown_top;
			if (y_alignment === "top") {
				dropdown_top = trigger_rect.top - dropdown_height;
			} else if (y_alignment === "bottom") {
				dropdown_top = trigger_rect.bottom;
			} else {
				dropdown_top =
					trigger_rect.top +
					(trigger_rect.height - dropdown_height) / 2;
			}

			// Ensure dropdown stays within viewport horizontally
			dropdown_abs_left = Math.max(dropdown_abs_left, 0);
			dropdown_abs_left = Math.min(
				dropdown_abs_left,
				window.innerWidth - dropdown_width
			);

			const left = dropdown_abs_left - container_rect.left;

			let top = Math.max(
				dropdown_top - container_rect.top,
				window.scrollY
			);

			if (top + dropdown_rect.height > window.innerHeight) {
				top = window.innerHeight - dropdown_rect.height;
			}

			dropdown_ref.current.style.left = `${left}px`;
			dropdown_ref.current.style.top = `${top}px`;

			dropdown_ref.current.style.opacity = "1";
		};

		setPosition();
	}, [is_active, props.x_alignment, props.y_alignment]);

	return (
		<div
			ref={trigger_ref}
			className={classNames("w-full", {
				"is-active": is_active,
				"is-right": props.x_alignment === "right",
			})}
		>
			<div className="dropdown-trigger w-full">
				<button
					className="flex w-full items-center gap-2"
					aria-haspopup="true"
					onClick={() =>
						!props.disabled ? setIsActive(true) : undefined
					}
				>
					{typeof props.children === "function"
						? props.children(is_active)
						: props.children}
				</button>
			</div>
			{is_active &&
				createPortal(
					<div
						id={props.id}
						ref={dropdown_ref}
						className="absolute z-[1002] w-fit p-2 opacity-0 transition-all"
						role="menu"
					>
						{props.dropdown_element}
					</div>,
					props.parent_id
						? document.getElementById(props.parent_id)
						: document.body
				)}
		</div>
	);
});
